import { useEffect, useState } from "react";
import {
  OrganizationMessageCountsDocument,
  OrganizationMessageDetailDocument,
  OrganizationMessagesDocument,
  OrganizationMessageStatus,
  type OrganizationMessageFieldsFragment,
  useCreateOrganizationMessageMutation,
  useOrganizationMessageReachQuery,
  useUpdateOrganizationMessageMutation,
} from "../../generated/graphql";
import { useConfirmDialog } from "../api-keys-admin/components/confirm-dialog";
import { throwOnGraphqlErrors } from "../api-keys-admin/throw-on-graphql-errors";
import {
  type AudienceState,
  audienceError,
  audienceFromMessage,
  audienceInput,
  contentInput,
  EMPTY_AUDIENCE,
  EMPTY_FORM,
  formError,
  formFromMessage,
  type MessageFormState,
} from "./message-content";
import useSendMessage from "./use-send-message";

export type When = "NOW" | "LATER";

const REFETCH_QUERIES = [
  OrganizationMessagesDocument,
  OrganizationMessageCountsDocument,
  OrganizationMessageDetailDocument,
];

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * State and actions of the message composer. A new message or a draft can be
 * saved as a draft, sent now, or scheduled; the draft is created first, so a
 * send the server refuses leaves a draft to retry from rather than nothing.
 */
const useMessageComposer = ({
  open,
  message,
  onSaved,
}: {
  readonly open: boolean;
  readonly message: OrganizationMessageFieldsFragment | null;
  readonly onSaved: (uuid: string) => void;
}) => {
  const [form, setForm] = useState<MessageFormState>(EMPTY_FORM);
  const [audience, setAudience] = useState<AudienceState>(EMPTY_AUDIENCE);
  const [when, setWhen] = useState<When>("NOW");
  const [startsAt, setStartsAt] = useState("");
  const [draftUuid, setDraftUuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { confirm, confirmDialog } = useConfirmDialog();
  const send = useSendMessage();
  const [createMessage] = useCreateOrganizationMessageMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  const [updateMessage] = useUpdateOrganizationMessageMutation({
    refetchQueries: REFETCH_QUERIES,
  });

  const isNewOrDraft =
    message === null || message.status === OrganizationMessageStatus.Draft;
  const audienceProblem = audienceError(audience);
  const { data: reachData, error: reachError } =
    useOrganizationMessageReachQuery({
      variables: { audience: audienceInput(audience) },
      skip: !open || !isNewOrDraft || audienceProblem !== null,
      errorPolicy: "all",
    });
  const reach = reachData?.organizationMessageReach;

  useEffect(() => {
    if (open) {
      setForm(message === null ? EMPTY_FORM : formFromMessage(message));
      setAudience(
        message === null ? EMPTY_AUDIENCE : audienceFromMessage(message),
      );
      setWhen("NOW");
      setStartsAt("");
      setDraftUuid(
        message?.status === OrganizationMessageStatus.Draft
          ? message.uuid
          : null,
      );
      setError(null);
    }
  }, [open, message]);

  const validate = (): string | null =>
    formError(form) ?? (isNewOrDraft ? audienceProblem : null);

  /** Creates or updates the draft (or the edited message); returns its uuid. */
  const save = async (): Promise<string> => {
    const content = contentInput(form);
    const existingUuid = draftUuid ?? message?.uuid ?? null;
    if (existingUuid === null) {
      const result = await createMessage({
        variables: { input: { ...content, audience: audienceInput(audience) } },
      });
      throwOnGraphqlErrors(result);
      const uuid = result.data?.createOrganizationMessage.uuid;
      if (uuid === undefined) {
        throw new Error("The message was not saved.");
      }
      setDraftUuid(uuid);
      return uuid;
    }
    throwOnGraphqlErrors(
      await updateMessage({
        variables: {
          input: {
            ...content,
            uuid: existingUuid,
            ...(isNewOrDraft ? { audience: audienceInput(audience) } : {}),
          },
        },
      }),
    );
    return existingUuid;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem !== null) {
      setError(problem);
      return;
    }
    if (
      message?.allCompanies === true &&
      message.status === OrganizationMessageStatus.Active
    ) {
      try {
        await confirm({
          title: "Change a live message to every company?",
          description:
            "Every company's users see the new content, severity and expiry at once.",
          confirmationText: "Save",
        });
      } catch {
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      onSaved(await save());
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    const problem =
      validate() ??
      (when === "LATER" && startsAt === ""
        ? "Choose when the message starts."
        : null);
    if (problem !== null) {
      setError(problem);
      return;
    }
    const start = when === "LATER" ? new Date(startsAt) : null;
    const confirmed = await send.confirmSend(
      { ...audience, severity: form.severity },
      reach,
      start,
    );
    if (confirmed === undefined) {
      return;
    }
    setSaving(true);
    setError(null);
    let uuid: string | null = null;
    try {
      uuid = await save();
      await send.deliver(uuid, confirmed.confirmation, start);
      onSaved(uuid);
    } catch (sendError) {
      setError(
        uuid === null
          ? errorMessage(sendError)
          : `Saved as a draft, but not sent: ${errorMessage(sendError)}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    setForm,
    audience,
    setAudience,
    audienceProblem,
    when,
    setWhen,
    startsAt,
    setStartsAt,
    reach,
    reachError,
    error,
    saving,
    isNewOrDraft,
    handleSave,
    handleSend,
    confirmDialog,
    sendConfirmDialog: send.confirmDialog,
  };
};

export default useMessageComposer;
